import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";
import CustomDatePicker from "./steps/CustomDatePicker";
import { createUserThunk, getUsersThunk, getUserByIdThunk } from "../../store/reducers/users/users.thunks";
import { getEscuelasThunk, getPlantelesThunk } from "../../store/reducers/users/users.thunks";
import { updateField, resetForm } from "../../store/reducers/users/usersForm.slice";
import { openViewModal } from "../../store/reducers/users/users.reducer";
import { generatePassword } from "../../utils/passwordGenerator";
import { toast } from 'react-toastify';

const UserFormWizard = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { escuelas, planteles } = useSelector((state) => state.users);

  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  // Cargar escuelas al montar
  useEffect(() => {
    dispatch(getEscuelasThunk());
  }, [dispatch]);

  // Cuando cambia escuela, carga sus planteles
  const handleEscuelaChange = (e) => {
    const escuelaId = Number(e.target.value);
    setValue("escuela", escuelaId, { shouldValidate: true });
    setValue("plantel", "", { shouldValidate: true });
    if (escuelaId) {
      dispatch(getPlantelesThunk(escuelaId));
    }
  };

  // Generar contraseña automática
  const autoPassword = () => {
    const pwd = generatePassword(10);
    setValue("password", pwd, { shouldValidate: true });
    setValue("password2", pwd, { shouldValidate: true });
    dispatch(updateField({ name: "password", value: pwd }));
    dispatch(updateField({ name: "password2", value: pwd }));
  };

  // Submit final
  const onSubmit = async (data) => {
    // Sincronizar todos los campos al store
    Object.entries(data).forEach(([name, value]) => {
      const finalValue = ["plantel", "escuela"].includes(name)
        ? Number(value) || null
        : value;
      dispatch(updateField({ name, value: finalValue }));
    });

    // Pequeña espera para que Redux procese los updates
    await new Promise((resolve) => setTimeout(resolve, 50));

    const result = await dispatch(createUserThunk());
    if (result.meta.requestStatus === "fulfilled") {
      const createdId = result.payload.data.id;
      dispatch(getUsersThunk({ page: 1, search: "" }));
      dispatch(resetForm());
      toast.success('Usuario creado exitosamente');
      if (onSuccess) {
        onSuccess();
      }
      await dispatch(getUserByIdThunk(createdId));
      dispatch(openViewModal());
    }
  };

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)} className="px-2 px-md-3">

          {/* ── Sección: Cuenta ── */}
          <p className="fw-bold text-primary mb-3 mt-2 fs-6 border-bottom pb-1">
            Información de cuenta
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Usuario <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${errors.username ? "is-invalid" : ""}`}
                placeholder="Ej: jperez"
                {...register("username", { required: "El usuario es requerido" })}
              />
              {errors.username && (
                <div className="invalid-feedback">{errors.username.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Email <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${errors.email ? "is-invalid" : ""}`}
                placeholder="Ej: jperez@mail.com"
                {...register("email", {
                  required: "El email es requerido",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Email no válido",
                  },
                })}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Password <span className="text-danger">*</span>
              </label>
              <div className="position-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control shadow-sm rounded-3 pe-5 ${errors.password ? "is-invalid" : ""}`}
                  placeholder="Contraseña"
                  {...register("password", {
                    required: "La contraseña es requerida",
                    minLength: { value: 8, message: "Mínimo 8 caracteres" },
                  })}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  className="position-absolute top-50 end-0 translate-middle-y me-2 cursor-pointer"
                  style={{ zIndex: 10 }}
                >
                  <Icon icon={showPassword ? "mdi-light:eye-off" : "mdi-light:eye"} width="22" />
                </span>
                {errors.password && (
                  <div className="invalid-feedback">{errors.password.message}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Confirmar password <span className="text-danger">*</span>
              </label>
              <div className="position-relative">
                <input
                  type={showPassword2 ? "text" : "password"}
                  className={`form-control shadow-sm rounded-3 pe-5 ${errors.password2 ? "is-invalid" : ""}`}
                  placeholder="Repite la contraseña"
                  {...register("password2", {
                    required: "Confirma la contraseña",
                    validate: (value) =>
                      value === getValues("password") || "Las contraseñas no coinciden",
                  })}
                />
                <span
                  onClick={() => setShowPassword2(!showPassword2)}
                  className="position-absolute top-50 end-0 translate-middle-y me-2 cursor-pointer"
                  style={{ zIndex: 10 }}
                >
                  <Icon icon={showPassword2 ? "mdi-light:eye-off" : "mdi-light:eye"} width="22" />
                </span>
                {errors.password2 && (
                  <div className="invalid-feedback">{errors.password2.message}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Rol <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${errors.rol ? "is-invalid" : ""}`}
                defaultValue=""
                {...register("rol", { required: "El rol es obligatorio" })}
              >
                <option value="" disabled>Selecciona un rol</option>
                <option value="ADMIN SYS">Administrador global</option>
                <option value="MENTOR">Mentor</option>
                <option value="ESTUDIANTE">Estudiante</option>
                <option value="ADMIN_ESCOLAR">Auxiliar de control escolar</option>
              </select>
              {errors.rol && (
                <div className="invalid-feedback">{errors.rol.message}</div>
              )}
            </div>

            <div className="col-md-6 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={autoPassword}
              >
                <Icon icon="mdi:refresh" width="18" className="me-1" />
                Generar contraseña
              </button>
            </div>
          </div>

          {/* ── Sección: Datos personales ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Datos personales
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Nombres <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${errors.nombres ? "is-invalid" : ""}`}
                placeholder="Ej: Juan"
                {...register("nombres", {
                  required: "El nombre es obligatorio",
                  minLength: { value: 2, message: "Mínimo 2 caracteres" },
                })}
              />
              {errors.nombres && (
                <div className="invalid-feedback">{errors.nombres.message}</div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Apellido paterno <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${errors.apellido_paterno ? "is-invalid" : ""}`}
                placeholder="Ej: Pérez"
                {...register("apellido_paterno", {
                  required: "El apellido paterno es obligatorio",
                })}
              />
              {errors.apellido_paterno && (
                <div className="invalid-feedback">{errors.apellido_paterno.message}</div>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold small">
                Apellido materno <span className="text-danger">*</span>
              </label>
              <input
                className={`form-control shadow-sm rounded-3 ${errors.apellido_materno ? "is-invalid" : ""}`}
                placeholder="Ej: García"
                {...register("apellido_materno", {
                  required: "El apellido materno es obligatorio",
                })}
              />
              {errors.apellido_materno && (
                <div className="invalid-feedback">{errors.apellido_materno.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <Controller
                name="fecha_nacimiento"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <CustomDatePicker
                    name={field.name}
                    value={field.value}
                    onChange={(_name, val) => field.onChange(val)}
                    error={fieldState.error}
                    label="Fecha de nacimiento (opcional)"
                  />
                )}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Estado civil <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${errors.estado_civil ? "is-invalid" : ""}`}
                defaultValue=""
                {...register("estado_civil", {
                  required: "El estado civil es obligatorio",
                })}
              >
                <option value="" disabled>Estado civil</option>
                <option value="SOLTERO">Soltero/a</option>
                <option value="CASADO">Casado/a</option>
                <option value="DIVORCIADO">Divorciado/a</option>
                <option value="VIUDO">Viudo/a</option>
                <option value="UNION_LIBRE">En unión libre</option>
                <option value="SEPARADO">Separado/a</option>
              </select>
              {errors.estado_civil && (
                <div className="invalid-feedback">{errors.estado_civil.message}</div>
              )}
            </div>
          </div>

          {/* ── Sección: Contacto ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Contacto
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">Teléfono</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: 5512345678"
                {...register("telefono")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">Correo alternativo</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: otro@mail.com"
                {...register("correo")}
              />
            </div>
          </div>

          {/* ── Sección: Institución ── */}
          <p className="fw-bold text-primary mb-3 mt-3 fs-6 border-bottom pb-1">
            Institución
          </p>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Escuela <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${errors.escuela ? "is-invalid" : ""}`}
                defaultValue=""
                {...register("escuela", { required: "La escuela es obligatoria" })}
                onChange={handleEscuelaChange}
              >
                <option value="" disabled>Selecciona una escuela</option>
                {escuelas.map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </select>
              {errors.escuela && (
                <div className="invalid-feedback">{errors.escuela.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">
                Plantel <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select shadow-sm rounded-3 ${errors.plantel ? "is-invalid" : ""}`}
                defaultValue=""
                {...register("plantel", { required: "El plantel es obligatorio" })}
                disabled={!planteles.length}
              >
                <option value="" disabled>Selecciona un plantel</option>
                {planteles.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              {errors.plantel && (
                <div className="invalid-feedback">{errors.plantel.message}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">CURP</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: PEGJ900101HDFRZN01"
                {...register("curp")}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold small">RFC</label>
              <input
                className="form-control shadow-sm rounded-3"
                placeholder="Ej: PEGJ900101ABC"
                {...register("rfc")}
              />
            </div>
          </div>

          {/* ── Acciones ── */}
          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-danger px-4"
              onClick={() => {
                dispatch(resetForm());
                if (onSuccess) onSuccess();
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary px-5"
              disabled={!isValid}
            >
              Guardar usuario
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormWizard;
